import { Controller } from 'stimulus';
import { loadStripe } from '@stripe/stripe-js';
import Appsignal from 'utils/appsignal';
import { csrfFetch } from 'utils/fetch';

const POLL_INTERVAL_MS = 2000;
const POLL_LIMIT_MS = 60000;
const RETRY_INTERVAL_MS = 2000;
// Must stay above StartPayment::IN_FLIGHT (1 minute).
const RETRY_LIMIT_MS = 70000;
const CREATE_FAILED_MESSAGE = "We couldn't start your payment. Please try again.";
const WALLET_RETRY_MESSAGE = 'Your last payment attempt is still being checked. Wait a minute, then try again.';
const DONE_STATES = ['completed', 'disputed'];
const FAILED_STATES = ['failed', 'abandoned', 'refunded', 'revoked', 'dispute_lost'];

// The one-time purchase checkout's payment step. Every amount comes from the server: the quote endpoint prices the
// code, the purchase endpoint prices the charge, and Pay refuses to confirm when the two disagree with the total shown.
export default class extends Controller {
  static targets = [
    'payStep', 'processing', 'slow', 'success',
    'listPriceRow', 'listPrice', 'discountRow', 'discountCode', 'discountLabel', 'discount', 'total',
    'codeToggle', 'codeForm', 'codeInput', 'codeError', 'applyButton', 'removeCode',
    'walletSection', 'wallet', 'card', 'savedCardSection', 'newCardSection', 'payMessage', 'payButton', 'payLabel', 'spinner',
    'recipient', 'fromName', 'note', 'sentTo'
  ];

  static values = {
    account: String,
    publicKey: String,
    country: { type: String, default: 'US' },
    currency: String,
    amountCents: Number,
    productName: String,
    quoteUrl: String,
    purchaseUrl: String,
    confirmUrl: String,
    statusUrl: String,
    // No default: a custom default makes hasSummaryUrlValue always true.
    summaryUrl: String,
    coupon: { type: String, default: '' },
    gift: { type: Boolean, default: false },
    savedCard: { type: Boolean, default: false },
    refreshOnSuccess: { type: Boolean, default: false }
  };

  connect() {
    this.busy = false;
    this.cardComplete = false;
    this.useSaved = this.savedCardValue;
    this.pollTimer = null;
    this.retryTimer = null;
    if (!this.giftValue) this.updateRemoveCode();
    this.setup();
  }

  async setup() {
    try {
      this.stripe = await loadStripe(this.publicKeyValue, { stripeAccount: this.accountValue });
      this.elements = this.stripe.elements();
      // The card field stays unmounted until a buyer with a saved card asks for it.
      if (!this.useSaved) this.mountCard();
      this.syncPayButton();
      await this.mountWallet();
    } catch (error) {
      Appsignal.sendError(error);
      this.showPayMessage("We couldn't load the payment form. Reload the page and try again.");
    }
  }

  disconnect() {
    clearTimeout(this.pollTimer);
    clearTimeout(this.retryTimer);
    this.paymentRequest = null;
  }

  mountCard() {
    if (this.cardElement) return;
    this.cardElement = this.elements.create('card');
    this.cardElement.mount(this.cardTarget);
    this.cardElement.on('change', (event) => {
      this.cardComplete = event.complete;
      this.showPayMessage(event.error ? event.error.message : '');
      this.syncPayButton();
    });
  }

  async mountWallet() {
    this.paymentRequest = this.stripe.paymentRequest({
      country: this.countryValue,
      currency: this.currencyValue,
      total: this.walletTotal(),
      requestPayerName: false,
      requestPayerEmail: false,
      disableWallets: ['link']
    });

    const available = await this.paymentRequest.canMakePayment();
    if (!available || (!available.applePay && !available.googlePay)) return;

    const button = this.elements.create('paymentRequestButton', { paymentRequest: this.paymentRequest });
    // The wallet sheet must not open for a gift with no recipient yet.
    button.on('click', (event) => {
      if (!this.recipientMissing()) return;
      event.preventDefault();
      this.recipientTarget.focus();
      this.showPayMessage("Enter the email address of the person you're gifting.");
    });
    button.mount(this.walletTarget);
    this.walletSectionTarget.classList.remove('hidden');
    this.walletSectionTarget.classList.add('flex');
    this.paymentRequest.on('paymentmethod', (event) => this.pay({ walletEvent: event }));
  }

  walletTotal() {
    return { label: this.productNameValue, amount: this.amountCentsValue };
  }

  recipientMissing() {
    return this.giftValue && !this.recipientTarget.value.trim();
  }

  syncPayButton() {
    const needsInput = this.useSaved ? !this.stripe : !this.cardComplete;
    this.payButtonTarget.disabled = this.busy || needsInput || this.recipientMissing();
  }

  useNewCard(event) {
    event?.preventDefault();
    this.useSaved = false;
    if (this.elements) this.mountCard();
    this.showCardMode();
    this.cardElement?.focus();
  }

  useSavedCard(event) {
    event.preventDefault();
    this.useSaved = true;
    this.showCardMode();
  }

  showCardMode() {
    this.savedCardSectionTarget.classList.toggle('hidden', !this.useSaved);
    this.newCardSectionTarget.classList.toggle('hidden', this.useSaved);
    this.newCardSectionTarget.classList.toggle('flex', !this.useSaved);
    this.showPayMessage('');
    this.syncPayButton();
  }

  // A gift is quoted with the gift promotion alone, so it never sends a code.
  quoteBody(coupon) {
    return this.giftValue ? { purpose: 'gift' } : { coupon };
  }

  // The server resolves the saved card itself; a wallet pays with its own card, never the saved one.
  purchaseBody({ wallet = false } = {}) {
    const body = this.giftValue ? this.giftBody() : { coupon: this.couponValue };
    if (this.useSaved && !wallet) body.use_saved_card = '1';
    return body;
  }

  giftBody() {
    this.sentTo = this.recipientTarget.value.trim();
    return {
      recipient_email: this.sentTo,
      from_name: this.fromNameTarget.value.trim(),
      message: this.noteTarget.value.trim()
    };
  }

  setBusy(busy) {
    this.busy = busy;
    this.spinnerTarget.classList.toggle('hidden', !busy);
    this.syncPayButton();
  }

  showPayMessage(text) {
    this.payMessageTarget.textContent = text;
  }

  async post(url, body = {}) {
    const response = await csrfFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await response.json().catch(() => ({}));
    return { response, data };
  }

  // The promo code

  showCode() {
    this.codeToggleTarget.classList.add('hidden');
    this.codeFormTarget.classList.remove('hidden');
    this.codeInputTarget.focus();
  }

  async applyCode() {
    const code = this.codeInputTarget.value.trim();
    if (!code) return this.removeCode();

    this.applyButtonTarget.disabled = true;
    this.codeErrorTarget.textContent = '';
    try {
      const { response, data } = await this.post(this.quoteUrlValue, { coupon: code });
      if (response.ok) {
        this.couponValue = code;
        this.renderQuote(data);
        this.updateRemoveCode();
      } else {
        this.codeErrorTarget.textContent = data.error || "We couldn't check that code. Please try again.";
      }
    } catch (error) {
      Appsignal.sendError(error);
      this.codeErrorTarget.textContent = "We couldn't check that code. Please try again.";
    } finally {
      this.applyButtonTarget.disabled = false;
    }
  }

  async removeCode() {
    this.codeErrorTarget.textContent = '';
    this.codeInputTarget.value = '';
    this.couponValue = '';
    this.updateRemoveCode();
    const { response, data } = await this.post(this.quoteUrlValue, { coupon: '' });
    if (response.ok) this.renderQuote(data);
  }

  updateRemoveCode() {
    this.removeCodeTarget.classList.toggle('hidden', !this.couponValue);
  }

  // The total and the wallet amount move only with a server quote.
  renderQuote(quote) {
    this.amountCentsValue = quote.amount_cents;
    this.listPriceTarget.textContent = quote.list_price;
    this.totalTarget.textContent = quote.amount;
    this.payLabelTarget.textContent = this.giftValue ? `Pay ${quote.amount} and send gift` : `Pay ${quote.amount}`;
    if (this.giftValue) this.listPriceRowTarget.classList.toggle('hidden', !quote.promotion);
    this.discountRowTarget.classList.toggle('hidden', !quote.promotion);
    if (quote.promotion) {
      this.discountLabelTarget.textContent = quote.promotion.label;
      this.discountTarget.textContent = `-${quote.discount}`;
      const code = quote.promotion.code;
      this.discountCodeTarget.textContent = code || '';
      this.discountCodeTarget.classList.toggle('hidden', !code);
    }
    this.paymentRequest?.update({ total: this.walletTotal() });
  }

  // Paying

  async pay({ walletEvent = null } = {}) {
    if (this.busy) return walletEvent?.complete('fail');

    this.setBusy(true);
    this.showPayMessage('');
    try {
      const body = this.purchaseBody({ wallet: !!walletEvent });
      const { data } = await this.post(this.purchaseUrlValue, body);
      await this.handleCreate(data, walletEvent, body);
    } catch (error) {
      Appsignal.sendError(error);
      walletEvent?.complete('fail');
      this.showPayMessage("We couldn't reach the payment processor. Please try again.");
      this.setBusy(false);
    }
  }

  async handleCreate(data, walletEvent, body) {
    switch (data.status) {
      case 'retry':
        // The wallet sheet can't be held open while the create is retried.
        if (walletEvent) {
          walletEvent.complete('fail');
          return this.stopWith(WALLET_RETRY_MESSAGE);
        }
        return this.retryCreate(body);
      case 'requires_payment':
        return this.confirmPayment(data, walletEvent, body);
      case 'already_owned':
      case 'succeeded':
        walletEvent?.complete('success');
        return this.showSuccess(data.purchase_id, { alreadyOwned: data.status === 'already_owned' });
      case 'processing':
        walletEvent?.complete('success');
        return this.startPolling(data.purchase_id);
      case 'saved_card_unavailable':
        walletEvent?.complete('fail');
        this.useNewCard();
        return this.stopWith(data.error);
      case 'gift_invalid':
        walletEvent?.complete('fail');
        this.recipientTarget.focus();
        return this.stopWith(data.error);
      case 'gift_from_name_invalid':
        walletEvent?.complete('fail');
        this.fromNameTarget.focus();
        return this.stopWith(data.error);
      case 'gift_message_invalid':
        walletEvent?.complete('fail');
        this.noteTarget.focus();
        return this.stopWith(data.error);
      case 'promotion_invalid':
        walletEvent?.complete('fail');
        this.showCode();
        this.codeErrorTarget.textContent = data.error;
        // The code no longer applies, so the total shown goes back to what the server would charge.
        this.couponValue = '';
        this.updateRemoveCode();
        await this.refreshQuote();
        return this.stopWith('That code no longer applies. Check the new total and pay again.');
      default:
        walletEvent?.complete('fail');
        return this.stopWith(data.error || CREATE_FAILED_MESSAGE);
    }
  }

  // A create that may still be running on the server is sent again, unchanged, until it settles, with the button kept busy.
  async retryCreate(body) {
    const startedAt = Date.now();
    while (Date.now() - startedAt < RETRY_LIMIT_MS) {
      await new Promise((resolve) => { this.retryTimer = setTimeout(resolve, RETRY_INTERVAL_MS); });
      const { data } = await this.post(this.purchaseUrlValue, body);
      if (data.status !== 'retry') return this.handleCreate(data, null, body);
    }
    return this.stopWith(CREATE_FAILED_MESSAGE);
  }

  stopWith(message) {
    this.showPayMessage(message);
    this.setBusy(false);
  }

  async confirmPayment(data, walletEvent, body) {
    // The buyer approves the displayed total, so a charge at any other amount is shown to them first, never paid.
    if (data.amount_cents !== this.amountCentsValue) {
      walletEvent?.complete('fail');
      await this.refreshQuote();
      return this.stopWith('The price changed. Check the new total and pay again.');
    }

    const result = await this.confirm(data.client_secret, walletEvent, body);

    if (result.error) {
      return this.stopWith(result.error.message);
    }
    return this.confirmOnServer(data.purchase_id);
  }

  // The saved card is already on the intent, so confirming with no payment method handles any 3DS challenge.
  confirm(clientSecret, walletEvent, body) {
    if (walletEvent) return this.confirmWallet(clientSecret, walletEvent);
    if (body.use_saved_card) return this.stripe.confirmCardPayment(clientSecret);
    return this.stripe.confirmCardPayment(clientSecret, { payment_method: { card: this.cardElement } });
  }

  // handleActions: false lets the wallet sheet close before any 3DS challenge opens.
  async confirmWallet(clientSecret, walletEvent) {
    const first = await this.stripe.confirmCardPayment(clientSecret, { payment_method: walletEvent.paymentMethod.id }, { handleActions: false });
    if (first.error) {
      walletEvent.complete('fail');
      return first;
    }
    walletEvent.complete('success');
    if (first.paymentIntent.status === 'requires_action') {
      return this.stripe.confirmCardPayment(clientSecret);
    }
    return first;
  }

  async refreshQuote() {
    const { response, data } = await this.post(this.quoteUrlValue, this.quoteBody(this.couponValue));
    if (response.ok) this.renderQuote(data);
  }

  async confirmOnServer(purchaseId) {
    const { data } = await this.post(this.confirmUrlValue.replace(':id', purchaseId));
    if (data.status === 'succeeded' || data.status === 'already_owned') return this.showSuccess(purchaseId);
    if (data.status === 'processing') return this.startPolling(purchaseId);
    return this.stopWith(data.error || "We couldn't confirm your payment. Please try again.");
  }

  // After Pay

  show(target) {
    [this.payStepTarget, this.processingTarget, this.slowTarget, this.successTarget].forEach((element) => {
      const visible = element === target;
      element.classList.toggle('hidden', !visible);
      element.classList.toggle('flex', visible);
    });
  }

  showSuccess(purchaseId, { alreadyOwned = false } = {}) {
    clearTimeout(this.pollTimer);
    // A self purchase goes to its summary; a gift and /live keep the in-place panel.
    if (this.hasSummaryUrlValue && purchaseId) {
      const url = new URL(this.summaryUrlValue.replace(':id', purchaseId), window.location.href);
      if (alreadyOwned) url.searchParams.delete('from');
      window.Turbo.visit(url.toString());
      return;
    }
    if (this.giftValue) this.sentToTarget.textContent = this.sentTo;
    this.show(this.successTarget);
    // /live swaps the page into the player and chat in place, like the follow gate; only a payment gets here, never the login code.
    if (this.refreshOnSuccessValue) window.Turbo.session.refresh(window.location.href);
  }

  startPolling(purchaseId) {
    this.show(this.processingTarget);
    const startedAt = Date.now();
    const poll = async () => {
      try {
        const response = await csrfFetch(this.statusUrlValue.replace(':id', purchaseId), { headers: { Accept: 'application/json' } });
        const { status } = await response.json();
        if (DONE_STATES.includes(status)) return this.showSuccess(purchaseId);
        if (FAILED_STATES.includes(status)) return this.paymentFailed();
      } catch (error) {
        Appsignal.sendError(error);
      }
      if (Date.now() - startedAt >= POLL_LIMIT_MS) return this.show(this.slowTarget);
      this.pollTimer = setTimeout(poll, POLL_INTERVAL_MS);
    };
    poll();
  }

  paymentFailed() {
    this.show(this.payStepTarget);
    this.stopWith("Your payment didn't go through. Try again.");
  }
}
